import { isMovedApiPath } from "@/config/movedApiPaths";

/**
 * Origin to prefix onto API paths when the caller runs on the server.
 *
 * Browser callers pass "" and use a relative URL — the page's own origin is
 * already correct. Server Components and route handlers that call our own API
 * over HTTP need an absolute URL, and there is no safe way to guess it.
 *
 * This deliberately has no fallback. The previous code inlined
 * `NEXT_PUBLIC_API_BASE_URL || "http://localhost:3000"` in 103 places; when the
 * variable was set to an empty string the falsy fallback took over silently, and
 * once local dev moved to `next dev --experimental-https` every server-side call
 * hit a plaintext URL against a TLS port. The handshake closed the socket and it
 * surfaced far from the cause as `TypeError: fetch failed`
 * (`UND_ERR_SOCKET: other side closed`). A missing origin is a configuration
 * error and must say so at the point of failure.
 */
export function resolveServerApiBaseUrl(): string {
  /*
   * Three names, in order, and the order is the fix.
   *
   * `NEXT_PUBLIC_API_BASE_URL` was doing two unrelated jobs: the origin a
   * Server Component prefixes onto an API call, and the PUBLIC WEB address that
   * `packages/email` puts into links it sends members. Those were the same
   * string while web served its own API. They are not any more — the API lives
   * on its own host — and one variable cannot be both without sending a member
   * who clicks an email link to an API origin.
   *
   * So server-side API calls get their own name and the old one keeps meaning
   * what emails need. The fallbacks make this additive: a deployment that sets
   * neither new name behaves exactly as before.
   *
   *   SERVER_API_ORIGIN         this, explicitly — server-side calls go direct
   *   API_ORIGIN                the rewrite already points at the API; reuse it
   *   NEXT_PUBLIC_API_BASE_URL  what every existing deployment has set today
   */
  const configured =
    process.env.SERVER_API_ORIGIN?.trim() ||
    process.env.API_ORIGIN?.trim() ||
    process.env.NEXT_PUBLIC_API_BASE_URL?.trim();

  if (configured) return configured.replace(/\/$/, "");

  throw new Error(
    "No server-side API origin is set. Server Components and route handlers " +
      "call our own API over HTTP and need an absolute origin, including the " +
      "protocol.\n\n" +
      "Set SERVER_API_ORIGIN (preferred) — e.g. https://api.thesukha.app on " +
      "preview, https://api.thesukha.co in production, https://localhost:3000 " +
      "for local dev with --experimental-https.\n\n" +
      "API_ORIGIN and NEXT_PUBLIC_API_BASE_URL are read as fallbacks, in that " +
      "order, so existing deployments keep working."
  );
}

/**
 * The address a MEMBER can click — for links in emails and anywhere else a URL
 * leaves the system.
 *
 * Deliberately separate from the API origin, and deliberately still reading
 * `NEXT_PUBLIC_API_BASE_URL`: that is the value every environment already has
 * set to a web origin, and `packages/email` has been using it this way all
 * along. Renaming it would be a nicer name and a worse migration.
 */
export function resolvePublicWebOrigin(): string {
  const configured =
    process.env.NEXT_PUBLIC_WEB_ORIGIN?.trim() ||
    process.env.NEXT_PUBLIC_API_BASE_URL?.trim();

  if (configured) return configured.replace(/\/$/, "");

  throw new Error(
    "No public web origin is set. Links that reach a member — email links, " +
      "shared URLs — need the address they can open in a browser, which is the " +
      "WEB host and never the API host. Set NEXT_PUBLIC_WEB_ORIGIN, or leave " +
      "NEXT_PUBLIC_API_BASE_URL pointing at the web origin as it does today."
  );
}

/**
 * The origin to SHOW for a public API path — a URL a person copies, not one
 * this app fetches.
 *
 * {@link resolveApiBaseUrl} cannot be rendered: it answers the server-side API
 * origin when there is no `window` and the browser's otherwise, so a Client
 * Component that prints it renders one URL on the server and another in the
 * browser, and React discards the server HTML (TX-5284). Neither was the right
 * text either — the server origin can be an internal address, the browser one
 * is relative.
 *
 * This reads only `NEXT_PUBLIC_*` values, inlined identically into both
 * bundles, so it gives the same absolute answer on both sides: the API host
 * when the browser goes direct for this path, the public web origin (which
 * proxies it) otherwise.
 */
export function resolvePublicApiOrigin(path: string): string {
  const apiOrigin = process.env.NEXT_PUBLIC_API_ORIGIN?.trim();
  if (apiOrigin && isMovedApiPath(path)) return apiOrigin.replace(/\/$/, "");
  return resolvePublicWebOrigin();
}

/**
 * Origin to prefix onto API paths, for ANY caller — the one implementation.
 *
 * ## Why this exists
 *
 * There were two. `lib/api/client.ts` read `NEXT_PUBLIC_API_ORIGIN` and fell
 * back to same-origin; `lib/utils.ts` returned `window.location.origin` and
 * ignored the variable entirely. `webApiFetcher` — which its own docstring
 * calls "everything" — imported the second, so most of the app could NEVER
 * reach `apps/api` directly no matter how the environment was configured.
 *
 * That is not a preference about routing, it is a bug with a plausible
 * explanation attached, and it hid a real one: with every call going to the web
 * origin, a rewrite that mangled bare collection paths 404'd the tasks list,
 * the playlists list and `PATCH /api/v1/user` while sub-paths kept working.
 *
 * ## The browser should go straight to the API
 *
 * Fewer hops, real CORS exercised on every request rather than only by the
 * extension, and a failure surfaces at the service that produced it instead of
 * inside a rewrite. The rewrite from the web origin stays, but as what it
 * actually is: a compatibility shim for the Chrome extension, which shipped
 * early pointing at the web origin and cannot be redeployed to follow us.
 *
 * Set `NEXT_PUBLIC_API_ORIGIN` to make the browser go direct; leave it unset
 * and calls stay same-origin and take the shim, which is what local dev wants.
 */
export function resolveApiBaseUrl(path?: string): string {
  if (typeof window === "undefined") return resolveServerApiBaseUrl();

  // Trimmed because an empty-but-present variable is how a copy-pasted
  // environment arrives, and `""` here must mean "same origin", not "https://".
  const apiOrigin = process.env.NEXT_PUBLIC_API_ORIGIN?.trim();
  if (!apiOrigin) return "";

  /*
   * Per PATH, not per app.
   *
   * Not everything moved. `/api/v1/settings/version` reports the WEB build's
   * own version and deliberately stayed behind (TX-4975) — sending it to the
   * API 404s against a service that never had that route, which is exactly what
   * happened the first time the browser went direct.
   *
   * A caller that passes no path gets the API origin, which is what every
   * existing call site meant when it asked for "the API base".
   */
  if (path !== undefined && !isMovedApiPath(path)) return "";

  return apiOrigin.replace(/\/$/, "");
}
